// frontend/src/utils/transferTypeMeta.js
/**
 * Single source of truth for AssetTransfer.transfer_type display metadata.
 *
 * Mirrors the backend choice tuple defined on
 *   asset_inventory.models.AssetTransfer.transfer_type
 * (see migrations 0001 / 0021 / 0032). When a new transfer_type is added on
 * the backend, add it here too — every chip / label / icon in the frontend
 * reads from this file.
 *
 * The `color` is a token consumed by `<InfoBadge>` (see DS Badge & Status
 * Indicators -> InfoBadge). InfoBadge handles the actual Tailwind classes
 * so we never inline `bg-*-100 text-*-700` in page files (per DS rule
 * "Badge & Status Indicators": no local hand-rolled badges, no inline
 * status-color Tailwind).
 *
 * Color choices follow the semantic axis of the type:
 *   - positive lifecycle event   -> green / indigo / emerald
 *   - in-flight movement / hold  -> blue / amber / teal
 *   - needs attention            -> orange
 *   - terminal lifecycle         -> gray / slate
 *   - exception / problem        -> red / rose
 */

const META = {
  initial:       { label: 'Assignment',           color: 'green'   },
  // Defensive alias: legacy rows occasionally surface `assignment`; treat as `initial`.
  assignment:    { label: 'Assignment',           color: 'green'   },
  transfer:      { label: 'Transfer',             color: 'blue'    },
  deployment:    { label: 'Deployment',           color: 'indigo'  },
  return:        { label: 'Return',               color: 'amber'   },
  repair:        { label: 'At Workshop',          color: 'orange'  },
  repair_return: { label: 'Returned from Workshop', color: 'teal'  },
  disposal:      { label: 'Disposed',             color: 'slate'   },
  termination:   { label: 'Terminated',           color: 'gray'    },
  lease_return:  { label: 'Returned to Lessor',   color: 'gray'    },
  access_deactivation: { label: 'Access Deactivated', color: 'rose' },
  lost:          { label: 'Lost',                 color: 'rose'    },
  found:         { label: 'Found',                color: 'emerald' },
  offboarding:   { label: 'Offboarding',          color: 'red'     },
};

const FALLBACK = { label: 'Unknown', color: 'gray' };

export const getTransferTypeMeta = (type) => META[type] || FALLBACK;

export const getTransferTypeLabel = (type) => getTransferTypeMeta(type).label;

export const getTransferTypeColor = (type) => getTransferTypeMeta(type).color;

const SNAKE_CASE = /^[a-z0-9_]+$/;

/**
 * Label for a transfer row.
 *
 * A known `transfer_type` always wins. The API display string is only a
 * fallback for a type this map has not learned yet, and only when it is
 * already human text. Django echoes the raw key (for example `lease_return`)
 * when the running process does not have that choice loaded; showing that
 * key, or the generic "Unknown" fallback, tells a custodian the audit row
 * has no meaning.
 *
 * @param {object|null|undefined} transfer
 * @returns {string}
 */
export function resolveTransferTypeLabel(transfer) {
  if (!transfer || typeof transfer !== 'object') return '';
  const type = transfer.transfer_type;
  if (type && META[type]) return META[type].label;
  const display = String(transfer.transfer_type_display || '').trim();
  if (display && META[display]) return META[display].label;
  if (display && display !== type && !SNAKE_CASE.test(display)) return display;
  if (type) return getTransferTypeLabel(type);
  return '';
}
